import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ordersAPI, authAPI } from '../api';
import AdsDisplay from '../components/AdsDisplay';
import { DollarSign, Package, CheckCircle, AlertCircle, CreditCard, Plus, List, MessageCircle, RefreshCw, Zap } from 'react-feather';
import { formatCurrencyWithConversion } from '../utils/currency';
import './Dashboard.css';

const STATUS_COLORS = {
  pending: 'badge-pending',
  in_progress: 'badge-in_progress',
  completed: 'badge-completed',
  partial: 'badge-partial',
  canceled: 'badge-canceled',
  refunded: 'badge-canceled',
};

function getDisplayStatus(order) {
  return order?.refund_status === 'refunded' || order?.refund_status === 'manual_refunded'
    ? 'refunded'
    : order?.status;
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [recentOrders, setRecentOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [whatsappNum, setWhatsappNum] = useState('+237600000000');

  useEffect(() => {
    authAPI.publicSettings().then(r => {
      const num = (r.data.settings?.whatsapp_number || process.env.REACT_APP_WHATSAPP || '+237600000000').replace(/\s/g, '');
      setWhatsappNum(num);
    }).catch(() => {
      setWhatsappNum((process.env.REACT_APP_WHATSAPP || '+237600000000').replace(/\s/g, ''));
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const [, res] = await Promise.all([
        refreshUser(),
        ordersAPI.getRecent(),
      ]);
      setRecentOrders(res.data.orders || []);
      setTotalOrders(res.data.total || 0);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => { load(); }, [load]);

  const balance = parseFloat(user?.balance || 0);
  const total = totalOrders;
  const completed = recentOrders.filter(o => getDisplayStatus(o) === 'completed').length;
  const active = recentOrders.filter(o => ['pending', 'in_progress'].includes(getDisplayStatus(o))).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 14 }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 15, marginTop: 2 }}>
            Welcome back, <strong style={{ color: 'var(--text)' }}>{user?.username}</strong>
          </p>
        </div>
        <div className="dashboard-action-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/dashboard/deposit" className="btn btn-orange dashboard-action-btn" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CreditCard size={18} /> Add Funds
          </Link>
          <Link to="/dashboard/orders/new" className="btn btn-primary dashboard-action-btn dashboard-new-order-btn" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plus size={18} /> New Order
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom: 28 }}>
        <div className="stat-card stat-accent-blue" style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', top: -20, right: -20, width: 100, height: 100,
            background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
            borderRadius: '50%', pointerEvents: 'none'
          }} />
          <div className="stat-icon" style={{ color: 'var(--accent)' }}><DollarSign size={32} /></div>
          <div className="stat-value">{formatCurrencyWithConversion(balance, user?.currency || 'USD')}</div>
          <div className="stat-label">Account Balance</div>
          <Link to="/dashboard/deposit" style={{
            display: 'inline-block', marginTop: 10, fontSize: 12,
            color: 'var(--accent)', textDecoration: 'none', fontWeight: 600
          }}>+ Top up →</Link>
        </div>

        <div className="stat-card stat-accent-orange">
          <div className="stat-icon" style={{ color: 'var(--orange)' }}><Package size={32} /></div>
          <div className="stat-value">{loading ? '—' : total}</div>
          <div className="stat-label">Recent Orders</div>
        </div>

        <div className="stat-card stat-accent-green">
          <div className="stat-icon" style={{ color: 'var(--green-500)' }}><CheckCircle size={32} /></div>
          <div className="stat-value">{loading ? '—' : completed}</div>
          <div className="stat-label">Completed</div>
        </div>

        <div className="stat-card stat-accent-purple">
          <div className="stat-icon" style={{ color: '#a855f7' }}><AlertCircle size={32} /></div>
          <div className="stat-value">{loading ? '—' : active}</div>
          <div className="stat-label">Active Orders</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid-3" style={{ marginBottom: 28 }}>
        <Link to="/dashboard/orders/new" style={{ textDecoration: 'none' }}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '20px 24px',
            display: 'flex', alignItems: 'center', gap: 16,
            transition: 'all 0.2s', cursor: 'pointer'
          }}
            onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--accent-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
              <Plus size={24} style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontFamily: 'Syne, sans-serif', fontSize: 15 }}>Place New Order</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Browse 1000+ services</div>
            </div>
          </div>
        </Link>

        <Link to="/dashboard/orders" style={{ textDecoration: 'none' }}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '20px 24px',
            display: 'flex', alignItems: 'center', gap: 16,
            transition: 'all 0.2s', cursor: 'pointer'
          }}
            onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--orange-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
              <List size={24} style={{ color: 'var(--orange)' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontFamily: 'Syne, sans-serif', fontSize: 15 }}>Track Orders</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>View all your orders</div>
            </div>
          </div>
        </Link>

        <a href={`https://wa.me/${whatsappNum}?text=Hello%20NiceBooster%20Support!`}
          target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '20px 24px',
            display: 'flex', alignItems: 'center', gap: 16,
            transition: 'all 0.2s', cursor: 'pointer'
          }}
            onMouseOver={e => { e.currentTarget.style.borderColor = '#25D366'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(37,211,102,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
              <MessageCircle size={24} style={{ color: '#25D366' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontFamily: 'Syne, sans-serif', fontSize: 15 }}>Live Support</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Chat on WhatsApp</div>
            </div>
          </div>
        </a>
      </div>

      {/* Ad 2 - After Quick Actions */}
      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />

      <AdsDisplay adSlot="banner-320x50" className="banner" />

      {/* Recent Orders */}
      <div className="card">
        <div className="dashboard-recent-header">
          <h3 style={{ fontSize: 18, fontFamily: 'Syne, sans-serif' }}>Recent Orders</h3>
          <div className="dashboard-recent-actions">
            <button className="btn btn-secondary btn-sm dashboard-recent-btn" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <RefreshCw size={16} /> Refresh
            </button>
            <Link to="/dashboard/orders" className="btn btn-secondary btn-sm dashboard-recent-btn" style={{ display: 'flex', alignItems: 'center' }}>View All →</Link>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : recentOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}><AlertCircle size={52} style={{ color: 'var(--text-muted)' }} /></div>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No orders yet</p>
            <p style={{ fontSize: 14, marginBottom: 20 }}>Place your first order to get started</p>
            <Link to="/dashboard/orders/new" className="btn btn-primary"><Zap size={16} style={{ marginRight: 6 }} /> Browse Services</Link>
          </div>
        ) : (
          <div className="table-wrap dashboard-recent-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Service</th>
                  <th>Quantity</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map(o => (
                  (() => {
                    const displayStatus = getDisplayStatus(o);
                    return (
                  <tr key={o.id}>
                    <td style={{ fontFamily: 'monospace', color: 'var(--text-dim)', fontSize: 13 }}>#{o.id}</td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 14, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o.service_name}
                      </div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{parseInt(o.quantity).toLocaleString()}</td>
                    <td style={{ color: 'var(--orange)', fontWeight: 700 }}>{formatCurrencyWithConversion(o.cost, user?.currency || 'USD', 4)}</td>
                    <td>
                      <span className={`badge ${STATUS_COLORS[displayStatus] || 'badge-pending'}`}>
                        {displayStatus.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
